export const COUNTRIES = [
  'France',
  'Belgium',
  'Switzerland',
  'Luxembourg',
  'Germany',
  'Spain',
] as const

export type Country = (typeof COUNTRIES)[number]

export const DEFAULT_COUNTRY: Country = 'France'

/** ISO 3166-1 alpha-2 → country label. */
export const COUNTRY_ISO_TO_LABEL: Record<string, Country> = {
  FR: 'France',
  BE: 'Belgium',
  CH: 'Switzerland',
  LU: 'Luxembourg',
  DE: 'Germany',
  ES: 'Spain',
}

/** Country label → ISO 3166-1 alpha-2 (Google Places region codes). */
export const COUNTRY_LABEL_TO_REGION_CODE: Record<Country, string> = {
  France: 'fr',
  Belgium: 'be',
  Switzerland: 'ch',
  Luxembourg: 'lu',
  Germany: 'de',
  Spain: 'es',
}

export const ALL_COUNTRY_REGION_CODES = COUNTRIES.map(
  country => COUNTRY_LABEL_TO_REGION_CODE[country]
)

export function isAllowedCountry(value: string): value is Country {
  return (COUNTRIES as readonly string[]).includes(value)
}

/** Normalize API country (ISO alpha-2 or label) to a supported country label. */
export function resolveCountryLabel(value: string | null | undefined): Country {
  if (!value) return DEFAULT_COUNTRY
  if (isAllowedCountry(value)) return value
  const mapped = COUNTRY_ISO_TO_LABEL[value.toUpperCase()]
  if (mapped) return mapped
  return DEFAULT_COUNTRY
}

/** Region codes for Google Places autocomplete; falls back to all allowed countries. */
export function regionCodesForCountry(country: string): string[] {
  if (isAllowedCountry(country)) {
    return [COUNTRY_LABEL_TO_REGION_CODE[country]]
  }
  return [...ALL_COUNTRY_REGION_CODES]
}

export const INVOICE_MODE = {
  SELECTED: 'selected',
  UNPAID: 'unpaid',
} as const

export type InvoiceBulkSendMode =
  (typeof INVOICE_MODE)[keyof typeof INVOICE_MODE]

export const BLOCKING_MODE = {
  WARNING: 'warning',
  HARD: 'hard',
} as const

export type BlockingMode = (typeof BLOCKING_MODE)[keyof typeof BLOCKING_MODE]

/** Extensions accepted for document uploads (aligned with API mimes validation). */
export const DOCUMENT_FILE_EXTENSIONS = [
  'pdf',
  'docx',
  'xlsx',
  'png',
  'jpg',
  'jpeg',
] as const

export type DocumentFileExtension = (typeof DOCUMENT_FILE_EXTENSIONS)[number]

/** File input `accept` attribute for document uploads. */
export const DOCUMENT_FILE_ACCEPT = '.pdf,.docx,.xlsx,.png,.jpg,.jpeg'

/**
 * Max size of a single uploaded document, mirroring the API's
 * `filesystems.max_document_kb` (10 MB). Enforced client-side at selection time
 * so an oversized file is rejected immediately instead of failing on save.
 */
export const MAX_DOCUMENT_SIZE_MB = 10
export const MAX_DOCUMENT_SIZE_BYTES = MAX_DOCUMENT_SIZE_MB * 1024 * 1024

export function isDocumentFileExtension(
  value: string
): value is DocumentFileExtension {
  return (DOCUMENT_FILE_EXTENSIONS as readonly string[]).includes(value)
}

const MIME_TO_EXTENSION: Record<string, string> = {
  'application/pdf': 'pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    'docx',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'application/vnd.ms-excel': 'xls',
  'application/msword': 'doc',
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
}

/**
 * Short display label for a document type (e.g. "XLSX", "PDF").
 * Prefers the filename extension; falls back to a MIME → extension map.
 */
export function formatDocumentFileType(
  fileName?: string | null,
  mimeType?: string | null
): string {
  const fromName = fileName?.split('.').pop()?.trim().toLowerCase()
  if (fromName && fromName !== fileName?.toLowerCase()) {
    return fromName.toUpperCase()
  }

  const fromMime = mimeType
    ? MIME_TO_EXTENSION[mimeType.toLowerCase()]
    : undefined
  if (fromMime) return fromMime.toUpperCase()

  if (mimeType) {
    const subtype = mimeType.split('/').pop()?.split('.').pop()?.trim()
    if (subtype) return subtype.toUpperCase()
  }

  return '—'
}
